import isElementVisible from 'utils/isElementVisible'

export enum IsVisibleWatcherNodeState {
  Visible = 'visible',

  Hidden = 'hidden',

  Unknown = '',
}

interface WatcherResult {
  index: number
  state: IsVisibleWatcherNodeState
  changed: boolean
}

/**
 * @param indexes: represent index of node that is visible in input array
 */

type CssPropertyWatcherCallback = (indexes: Array<WatcherResult>) => void

interface InitialProps {
  targetSelectors: Array<string>
  checkResultCallback: CssPropertyWatcherCallback
}

export default class IsVisibleWatcher {
  targetSelectors: Array<string>
  checkResultCallback: CssPropertyWatcherCallback
  observer?: MutationObserver
  interval?: NodeJS.Timeout
  previousStates: Array<string> = []

  constructor(initial: InitialProps) {
    const { targetSelectors, checkResultCallback } = initial
    this.targetSelectors = targetSelectors
    this.checkResultCallback = checkResultCallback
    this.init()

    this.previousStates = targetSelectors.map(
      () => IsVisibleWatcherNodeState.Unknown
    )
  }

  processNode = (selector: string, index: number): WatcherResult => {
    const targetNode = document.querySelector(selector)

    const newIsVisible = isElementVisible(targetNode)

    const newState: IsVisibleWatcherNodeState = newIsVisible
      ? IsVisibleWatcherNodeState.Visible
      : IsVisibleWatcherNodeState.Hidden

    const previousState = this.previousStates?.[index]

    this.previousStates[index] = newState

    return {
      index,
      changed: newState !== previousState,
      state: newIsVisible
        ? IsVisibleWatcherNodeState.Visible
        : IsVisibleWatcherNodeState.Hidden,
    }
  }

  init() {
    if (window.MutationObserver) {
      // Modern browsers - use MutationObserver
      this.observer = new MutationObserver(() => {
        this.checkVisibility()
      })

      this.observer.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true,
      })
    } else {
      // Fallback for older browsers - use setInterval
      this.interval = setInterval(() => {
        this.checkVisibility()
      }, 100)
    }

    // Run initial check
    this.checkVisibility()
  }

  checkVisibility() {
    const { targetSelectors, checkResultCallback } = this

    if (targetSelectors.length === 0) {
      checkResultCallback(
        Array.from(targetSelectors.keys()).map((index) => ({
          index,
          changed: false,
          state: IsVisibleWatcherNodeState.Hidden,
        }))
      )
      return
    }

    const indexesOfTriggered: Array<WatcherResult> = targetSelectors.map(
      this.processNode
    )

    if (indexesOfTriggered.every(({ changed }) => !changed)) {
      return
    }

    checkResultCallback(indexesOfTriggered)
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    if (this.interval) {
      clearInterval(this.interval)
    }
  }
}
